import { reactive } from 'vue';
import {
  AvailableViews,
  CachedFrame,
  SidebarMenuItem,
  TopLevelNavigation,
  TopLevelNavigationItem,
  User,
} from '@apparatix/types';

interface AppState {
  currentUser: User;
  realUser: User;
  companyLogo: string;
  isImpersonating: boolean;
  favoriteMenuItemIds: number[];
  recentMenuItemIds: number[];
  headerColor: string;
  tenantId: string;
  defaultPage: string;
  navigationItems: TopLevelNavigation;
  overflowNavigation: TopLevelNavigation;
  activePage?: TopLevelNavigationItem;
  activeChildren: Array<SidebarMenuItem>;
  activeSubPage?: SidebarMenuItem;
  loadedFrames: Array<CachedFrame>;
  rootView: AvailableViews;
  activeFrameUrl: string;
  isFrameLoading: boolean;
  shouldShowDashboard: boolean;
  showPageNotImplemented: boolean;
  shouldRefreshDashboard: boolean;
  shouldShowDashboardNotificationToast: boolean;
  setActiveChildren: (newChildren: Array<SidebarMenuItem>) => void;
  setActivePage: (newPage: TopLevelNavigationItem, skipChildLoad?: boolean) => void;
  setActiveSubPage: (newPage: SidebarMenuItem, skipVueRouteSetup?: boolean) => void;
  setCurrentUser: (user: User) => void;
  setActiveUrl: (url: string, navigationKey: string) => void;
  getPagesBySystemId: (id?: number, parentNavigationKey?: string) => [TopLevelNavigationItem?, SidebarMenuItem?];
  setActivePageByTitle: (title: string) => void;
  reconcileNavigation: (newNavigation: TopLevelNavigation) => void;
  setMenuShortcuts: (favoriteMenuItemIds: number[], recentMenuItemIds: number[]) => void;
  navigateToMenuItem: (systemId: number, parentNavigationKey?: string) => boolean;
  refreshDashboard: () => void;
  refreshCurrentPage: () => void;
  showDashboardNotificationToast: () => void;
  getHelpForCurrentPageUrl: () => string | null;
  getCurrentIframeWindow: () => Window | null;
}

type MenuItemActivationHandler = (menuItem: SidebarMenuItem) => void;
const menuItemActivationHandlers = new Set<MenuItemActivationHandler>();

export const registerMenuItemActivationHandler = (handler: MenuItemActivationHandler): (() => void) => {
  menuItemActivationHandlers.add(handler);

  return () => menuItemActivationHandlers.delete(handler);
};

const notifyMenuItemActivated = (menuItem: SidebarMenuItem): void => {
  menuItemActivationHandlers.forEach(handler => handler(menuItem));
};

export const AppStore: AppState = reactive<AppState>({
  currentUser: {} as User,
  realUser: {} as User,
  companyLogo: '',
  defaultPage: 'Dashboard',
  isImpersonating: false,
  favoriteMenuItemIds: [],
  recentMenuItemIds: [],
  headerColor: 'rgba(0, 0, 0, 0.84)',
  tenantId: '',
  navigationItems: [] as TopLevelNavigation,
  overflowNavigation: [] as TopLevelNavigation,
  activePage: undefined,
  activeChildren: [],
  activeSubPage: undefined,
  loadedFrames: [] as Array<CachedFrame>,
  rootView: 'legacy',
  activeFrameUrl: '',
  isFrameLoading: false,
  shouldShowDashboard: false,
  showPageNotImplemented: false,
  shouldRefreshDashboard: false,
  shouldShowDashboardNotificationToast: false,
  setActiveChildren(newChildren: Array<SidebarMenuItem>): void {
    this.activeChildren = newChildren;
  },
  setActivePage(newPage: TopLevelNavigationItem, skipChildLoad: boolean = false): void {
    this.activePage = newPage;

    // Hide the new notice toast automatically when navigating to the dashboard
    if (newPage.label === 'Dashboard' && this.shouldShowDashboardNotificationToast) {
      this.shouldShowDashboardNotificationToast = false;
    }

    if (skipChildLoad) {
      return;
    }

    // Remember the last sidebar page visited in this category
    if (newPage.lastChildNavigationKey && !skipChildLoad) {
      const child = newPage.children.find(c => c.navigationKey === newPage.lastChildNavigationKey);
      child?.command();
    } else {
      // Open the category home page or default home page
      const defaultPage = getDefaultPage(newPage.children);
      defaultPage?.command();
    }

    if (newPage.label.toLocaleLowerCase() === 'dashboard' && this.shouldRefreshDashboard) {
      this.shouldRefreshDashboard = false;
      refreshIframe('/iframe-wrapper/landing/dashboard');
    }

    this.setActiveChildren(newPage.children);
  },
  setActiveSubPage(newPage: SidebarMenuItem, skipVueRouteSetup?: boolean): void {
    notifyMenuItemActivated(newPage);

    if (newPage.rootView) {
      this.rootView = newPage.rootView;
    } else {
      this.rootView = 'legacy';
    }

    if (this.activeSubPage?.navigationKey === newPage.navigationKey) {
      this.refreshCurrentPage();
      return;
    }
    this.activeSubPage = newPage;

    if (this.rootView === 'legacy') {
      this.setActiveUrl(newPage.url, newPage.navigationKey);
    }

    const parentIdx = this.navigationItems.findIndex(item => item.navigationKey === newPage.parentNavigationKey);
    if (parentIdx === -1) return;

    this.navigationItems[parentIdx].lastChildNavigationKey = newPage.navigationKey;

    if (typeof newPage.routingCommand === 'function' && !skipVueRouteSetup) {
      newPage.routingCommand();
    }
  },
  setCurrentUser(user: User): void {
    this.currentUser = user;
  },
  setActiveUrl(url: string, navigationKey: string): void {
    if (this.activeFrameUrl === url) return;
    this.activeFrameUrl = url;
    this.isFrameLoading = true;

    if (url === '') {
      this.isFrameLoading = false;
      return;
    }

    const urlIndex = this.loadedFrames.findIndex(item => item.url === url);
    if (urlIndex === -1) {
      this.loadedFrames.push({
        url: url,
        timestamp: new Date(),
        isExpired: false,
        sidebarNavigationKey: navigationKey,
      });
    } else {
      this.loadedFrames[urlIndex].timestamp = new Date();
      this.loadedFrames[urlIndex].isExpired = false;
      this.isFrameLoading = false;
    }
  },
  getPagesBySystemId(id?: number, parentNavigationKey?: string): [TopLevelNavigationItem?, SidebarMenuItem?] {
    if (typeof id !== 'number') {
      console.warn('Invalid value provided to page lookup. Id must be a number');
      return [undefined, undefined];
    }

    let parent: TopLevelNavigationItem | undefined;
    let child: SidebarMenuItem | undefined;

    const candidateParents = parentNavigationKey
      ? this.navigationItems.filter(parentItem => parentItem.navigationKey === parentNavigationKey)
      : this.navigationItems;

    candidateParents.forEach(parentItem => {
      const childItem = parentItem.children?.find(_child => _child.systemId === id);

      if (childItem) {
        parent = parentItem;
        child = childItem;
        return;
      }
    });

    return [parent, child];
  },
  setActivePageByTitle(title: string): void {
    if (title.toLowerCase() === this.activePage?.label.toLowerCase()) {
      return;
    }

    const page = this.navigationItems.find(item => item.label.toLowerCase() === title.toLowerCase());
    if (page) {
      this.setActivePage(page);
    }
  },
  reconcileNavigation(newNavigation: TopLevelNavigation): void {
    const previousNavigation = this.navigationItems;
    const activeSystemId = this.activeSubPage?.systemId;
    const activeNavigationKey = this.activeSubPage?.navigationKey;
    const activeUrl = this.activeSubPage?.url;
    const hadActiveItem = Boolean(this.activeSubPage?.navigationKey);

    newNavigation.forEach(parent => {
      const previousParent = previousNavigation.find(item => item.label === parent.label);
      if (previousParent?.lastChildNavigationKey && parent.children.some(child => child.navigationKey === previousParent.lastChildNavigationKey)) {
        parent.lastChildNavigationKey = previousParent.lastChildNavigationKey;
      }
    });

    const allowedItemNavigationKeys = new Set(newNavigation.flatMap(parent => parent.children.map(child => child.navigationKey)));
    this.loadedFrames = this.loadedFrames.filter(frame => allowedItemNavigationKeys.has(frame.sidebarNavigationKey));
    this.navigationItems = newNavigation;
    this.overflowNavigation = [];

    const [retainedParent, retainedChild] = this.getPagesBySystemId(activeSystemId);
    if (retainedParent && retainedChild) {
      retainedParent.lastChildNavigationKey = retainedChild.navigationKey;
      this.activePage = retainedParent;
      this.activeChildren = retainedParent.children;
      this.activeSubPage = retainedChild;
      this.rootView = retainedChild.rootView || 'legacy';

      if (activeNavigationKey === retainedChild.navigationKey && activeUrl !== retainedChild.url) {
        this.loadedFrames = this.loadedFrames.filter(frame => frame.sidebarNavigationKey !== retainedChild.navigationKey);
        this.activeFrameUrl = '';

        if (this.rootView === 'legacy') {
          this.setActiveUrl(retainedChild.url, retainedChild.navigationKey);
        }
      }

      return;
    }

    if (!hadActiveItem) {
      return;
    }

    const fallbackPage = newNavigation.find(page => page.label.toLowerCase() === 'dashboard') ?? newNavigation[0];
    if (fallbackPage) {
      this.setActivePage(fallbackPage);

      return;
    }

    this.activePage = undefined;
    this.activeChildren = [];
    this.activeSubPage = undefined;
    this.rootView = 'legacy';
    this.setActiveUrl('', '');
  },
  setMenuShortcuts(favoriteMenuItemIds: number[], recentMenuItemIds: number[]): void {
    this.favoriteMenuItemIds = [...new Set(favoriteMenuItemIds)];
    this.recentMenuItemIds = recentMenuItemIds
      .filter((systemId, index, ids) => ids.indexOf(systemId) === index)
      .filter(systemId => !this.favoriteMenuItemIds.includes(systemId))
      .slice(0, 10);
  },
  navigateToMenuItem(systemId: number, parentNavigationKey?: string): boolean {
    const [parent, child] = this.getPagesBySystemId(systemId, parentNavigationKey);
    if (!parent || !child) {
      return false;
    }

    this.setActivePage(parent, true);
    this.setActiveChildren(parent.children);
    child.command?.();

    return true;
  },
  refreshDashboard() {
    refreshIframe('/iframe-wrapper/landing/dashboard');
  },
  refreshCurrentPage() {
    if (!this.activeSubPage) {
      return;
    }

    if (this.rootView === 'legacy') {
      this.isFrameLoading = true;
    }

    this.activeSubPage.url = this.activeSubPage.originalUrl;

    this.setActiveUrl(this.activeSubPage.url, this.activeSubPage.navigationKey);
    refreshIframe(this.activeSubPage.url);
  },
  showDashboardNotificationToast() {
    if (this.activePage?.label === 'Dashboard') return;

    this.shouldShowDashboardNotificationToast = true;
  },
  getHelpForCurrentPageUrl(): string | null {
    if (!this.activeSubPage) return null;
    return `/help/wwh_core/${this.activeSubPage.label}/${this.activeSubPage.systemId}`;
  },
  getCurrentIframeWindow(): Window | null {
    if (!this.activeSubPage) {
      return null;
    }

    const frame = document.querySelector(`iframe[src=${this.activeSubPage.url}]`) as HTMLIFrameElement;
    return frame?.contentWindow ?? null;
  },
});

const getDefaultPage = (sidebarItems: Array<SidebarMenuItem>): SidebarMenuItem => {
  if (!sidebarItems.length) return null;

  const explicitHomePage = sidebarItems.find(item => item.isHomePage);
  if (explicitHomePage) {
    return explicitHomePage;
  }

  const defaultHomePage = sidebarItems.find(item => item.isCategoryHome);
  if (defaultHomePage) {
    return defaultHomePage;
  }

  return sidebarItems[0];
};

const refreshIframe = (src: string) => {
  const iframe = document.querySelector(`iframe[src="${src}"]`) as HTMLIFrameElement;
  iframe?.contentWindow.location.replace(src);
};
